import React from "react";
import { View, StyleSheet, Text } from "react-native";
import { Card } from "react-native-paper";
import { Ionicons } from "@expo/vector-icons";

import { KButton, cardTypography } from "components";
import { Routine } from "types/routines";
import { COLORS } from "styles";
import { useNavigation } from "@react-navigation/native";
import { useDimensionsContext, useLayoutContext, Layouts } from "contexts";
import { useSelector } from "redux/store";
import { AnimatedInviteCard } from "components/HabitInvites/AnimatedInviteCard";
import { TouchableOpacity } from "react-native-gesture-handler";
import { useHistory } from "utils/react-router";
import { shallowEqual } from "react-redux";
import { logTapRoutineInviteCard } from "utils/analytics";
import { RoutineTags } from "components/Routines/RoutineTags";

export const RoutineInviteCard = ({
  id,
  isShown,
  hideItem,
  showDismissButton = true,
}: {
  id: string;
  isShown;
  hideItem: (id: string) => void;
  showDismissButton?: boolean;
}) => {
  const navigation = useNavigation();
  const history = useHistory();
  const layout = useLayoutContext();
  const { width, getScaledFontSize } = useDimensionsContext();
  const r: Routine = useSelector(
    state => state.firestore.data.routines[id],
    shallowEqual
  );

  // This shouldn't be needed but when Routines order changes,
  // redux-firestore has a bug where the id is undefined
  // https://github.com/prescottprue/redux-firestore/issues/292
  if (!r) {
    return null;
  }

  const onPress = () => {
    logTapRoutineInviteCard("learn_more", id);
    if (layout === Layouts.MOBILE) {
      navigation.navigate("RoutineOverviewScreen", {
        routineId: id,
      });
    } else {
      history.push(`/routine/${id}`);
    }
  };

  return (
    <AnimatedInviteCard isShown={isShown} width={width}>
      <TouchableOpacity onPress={onPress}>
        <Card.Cover source={{ uri: r.photoUrl }} style={styles.cover} />
        <Card.Title
          title={r.name}
          titleStyle={cardTypography(getScaledFontSize(1)).cardTitle}
          right={() => (
            <Ionicons
              name="ios-arrow-forward"
              size={15}
              style={styles.arrowIcon}
            />
          )}
        />
      </TouchableOpacity>
      <Card.Content>
        <Text
          numberOfLines={2}
          style={[
            cardTypography(getScaledFontSize(1)).cardContent,
            styles.cardContent,
          ]}
        >
          {r.description}
        </Text>
        <View style={styles.tagContainer}>
          <RoutineTags tags={r.tags} />
        </View>
      </Card.Content>
      <Card.Actions style={styles.cardActions}>
        {showDismissButton && (
          <KButton
            label="Dismiss"
            mode="text"
            color={COLORS.SALMON}
            onPress={() => {
              logTapRoutineInviteCard("dismiss", id);
              hideItem(id);
            }}
          />
        )}
        <KButton
          label="Learn More"
          mode="outlined"
          color={COLORS.PRIMARY}
          onPress={onPress}
          labelStyle={styles.buttonPositiveLabel}
          style={styles.buttonPositive}
        />
      </Card.Actions>
    </AnimatedInviteCard>
  );
};

const styles = StyleSheet.create({
  cover: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  arrowIcon: { paddingRight: 16, paddingLeft: 8 },
  cardContent: {
    marginTop: -12,
  },
  cardActions: {
    paddingBottom: 24,
    justifyContent: "center",
  },
  buttonPositiveLabel: {
    fontWeight: "bold",
  },
  buttonPositive: {
    borderColor: COLORS.PRIMARY,
  },
  tagContainer: {
    marginTop: 8,
  },
});
